// ════════════════════════════════════════════════════════════════
// generatePayloadForScreen.js
// Centralized pure function that builds the Myra payload for each
// GSA screen. Takes (screenName, assessmentData), returns payload.
// ════════════════════════════════════════════════════════════════

import { MESSAGING, TEST_MAP, getNegativeTest } from './GSA_CONSTANTS';
import MyraInteraction from "../self-assesment/MyraInteractionController";

// ── Helpers ──────────────────────────────────────────────────

export function getUserConditions() {
  try {
    const raw = localStorage.getItem('userCondition');
    if (!raw) return { pre_existing: [], current: [] };
    const parsed = JSON.parse(raw);
    return parsed.usercondition || { pre_existing: [], current: [] };
  } catch {
    return { pre_existing: [], current: [] };
  }
}

export function parseDataAfterInfo() {
  try {
    let raw = localStorage.getItem('dataAfterInfo');
    if (!raw) return { rom: [], dur: [] };
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    const rom = parsed.map(rep => rep[0]);
    const dur = parsed.map(rep => rep[3]);
    return { rom, dur };
  } catch {
    return { rom: [], dur: [] };
  }
}

function getInputMode() {
  return MyraInteraction.getMode() || 'button';
}

function buildBase(screenName) {
  return {
    gsa_step: screenName,
    user_name: localStorage.getItem('self_name') || '',
    input_mode: getInputMode(),
    user_conditions: getUserConditions(),
  };
}

function buildCurrentUnderstandingBase(assessmentData) {
  return {
    pain_locations: assessmentData.selectedZones || [],
  };
}

export function buildCurrentUnderstandingFull(assessmentData) {
  const subtype = assessmentData.probableSubtype;
  const diagnosis = subtype ? MESSAGING.diagnosis[subtype] : null;

  return {
    pain_locations: assessmentData.selectedZones || [],
    relief_factors: diagnosis?.relief || null,
    perceived_subtype: subtype || null,
    pattern_description: diagnosis?.implications || null,
  };
}

function buildTest1Details(assessmentData) {
  const subtype = assessmentData.probableSubtype || 'NEUTRAL_SENSITIVE';
  const test = TEST_MAP[subtype];
  if (!test) return [];
  return [{
    name: test.name,
    subtype: subtype,
    video: test.video,
    testNumber: 1,
  }];
}

function buildTest2Details(assessmentData) {
  const subtype = assessmentData.probableSubtype || 'NEUTRAL_SENSITIVE';
  const problemArea = assessmentData.problemArea;
  const test2 = getNegativeTest(subtype, problemArea);
  if (!test2) return [];
  return [{
    name: test2.name,
    subtype: subtype,
    video: test2.video,
    testNumber: 2,
  }];
}

function buildTestResults1() {
  const data = parseDataAfterInfo();
  return {
    movement_test_1: {
      rom: data.rom,
      dur: data.dur,
    },
  };
}

function buildTestResults2(assessmentData) {
  const test1Results = buildTestResults1();
  return {
    ...test1Results,
    movement_test_2: {
      feedback2: assessmentData.test2?.feedback2 || null,
      skipped: assessmentData.test2?.skipped || false,
    },
  };
}


// ── Main Export ──────────────────────────────────────────────

export function generatePayloadForScreen(screenName, assessmentData) {
  const base = buildBase(screenName);
  const data = assessmentData || {};

  switch (screenName) {

    // ── Intro / landing (pre-assessment) ──
    case 'landing':
      return base;

    // ── Early screens (base only) ──
    case 'safety_screen':
    case 'problem_area':
    case 'pain_location':
      return base;

    // ── Safety screening ──
    // Sends pain_locations so Claude has context, plus the user's
    // checked safety conditions (labels + IDs) when available.
    // safety_selections is empty [] on initial mount and populated
    // after the user submits the checklist.
    case 'SafetyScreeningScreen':
      return {
        ...base,
        current_understanding: {
          pain_locations: data.selectedZones || [],
        },
        safety_selections: data.safetySelections || [],
        safety_selection_ids: data.safetySelectionIds || [],
      };

    // ── CPG questions ──
    case 'cpg_questions':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingBase(data),
      };

    // ── Subtype, self-assessment, NPRS (full understanding) ──
    case 'subtype_questioning':
    case 'self_assessment':
    case 'nprs_symptoms':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingFull(data),
      };

    // ── Test 1 flow: demo, camera, movement, self-report, RPE ──
    case 'test1_demo':
    case 'camera_permission':
    case 'movement_test_1':
    case 'self_report_compare':
    case 'self_report_speed':
    case 'mode_selection':
    case 'test1_rpe':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingFull(data),
        current_test: buildTest1Details(data),
      };

    // ── Test 1 symptoms (adds test results) ──
    case 'test1_symptoms':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingFull(data),
        current_test: buildTest1Details(data),
        test_results: buildTestResults1(),
      };

    // ── Test 2 flow: demo, movement ──
    case 'test2_demo':
    case 'movement_test_2':
      return {
        ...base,
        current_test: buildTest2Details(data),
        test_results: buildTestResults1(),
      };

    // ── Symptom2 / Feedback2 (both tests) ──
    case 'symptom2':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
      };

    // ── Personalize ──
    case 'personalize':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
      };

    // ── Recovery Score (adds userProfile) ──
    case 'recovery_score':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
        userProfile: {
          ...(data.biometrics || {}),
          ...(data.signature || {}),
        },
      };

    // ── Plan Screen (adds currentPlan) ──
    case 'plan_screen':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
        userProfile: {
          ...(data.biometrics || {}),
          ...(data.signature || {}),
        },
        currentPlan: data.planDetails || null,
      };

    default:
      return base;
  }
}
